import { produce } from 'immer';
import {
  startReadSheetGroup
} from '../../../storage/managers/ReadManager';
import { SheetGroup } from '../../../types';
import {
  AddSheetToGroup,
  CreateSheetGroup,
  DeleteSheetGroup,
  RemoveSheetFromGroup,
} from '../../../types/commands/sheetGroupsCommands/sheetGroupCommands';
import { SheetManagementSet } from '../../../types/sheet/draft';
import {
  addSheetToGroupCommand,
  createGroupCommand,
  deleteGroupCommand,
  removeSheetFromGroupCommand,
} from '../generate/generateSheetGroupsCommands';
import {
  addEntityIdToName,
  removeFromEntityIdToName,
} from '../../entityIdToName/entityIdToName';

// #region create sheet group
export async function createSheetGroup(
  set: SheetManagementSet,
  command: CreateSheetGroup
) {
  const { group } = command;

  set.updatedGroups.set(group.id, group);

  createGroupCommand(set, group);

  addEntityIdToName(set, group, true);

  return group;
}
// #endregion

// #region add sheet to group
export async function addSheetToGroup(
  set: SheetManagementSet,
  command: AddSheetToGroup
) {
  const { groupId, sheetId } = command;
  const entityIdToNameMap =
    set.updatedEntityIdToNameMap ?? set.staticEntityIdToNameMap;

  const doesGroupExists = !!entityIdToNameMap.groupsIdToName[groupId];

  let group: SheetGroup | undefined;
  if (doesGroupExists) {
    group = await getSheetGroup(set, groupId);
  }

  if (group && !group.sheets.includes(sheetId)) {
    const updatedGroup = produce(group, (draft) => {
      draft.sheets.push(sheetId);
    });
    set.updatedGroups.set(groupId, updatedGroup);
  }

  addSheetToGroupCommand(set, groupId, sheetId);
}
// #endregion

// #region delete sheet group
export async function deleteSheetGroup(
  set: SheetManagementSet,
  command: DeleteSheetGroup
) {
  const { groupId } = command;
  const doesGroupExists = checkIfGroupExists(set, groupId);
  if (doesGroupExists) {
    const group: SheetGroup | undefined = await getSheetGroup(set, groupId);

    if (group) deleteGroupCommand(set, group);

    set.deletedGroups.add(groupId);

    removeFromEntityIdToName(set, groupId, true);
  }
}
// #endregion

// #region remove sheet from group
export async function removeSheetFromGroup(
  set: SheetManagementSet,
  command: RemoveSheetFromGroup
) {
  const { groupId, sheetId } = command;

  const doesGroupExists = checkIfGroupExists(set, groupId);

  if (doesGroupExists) {
    const group: SheetGroup | undefined = await getSheetGroup(set, groupId);
    if (group) {
      const updatedGroup = produce(group, (draft) => {
        draft.sheets = draft.sheets.filter((id) => id !== sheetId);
      });
      set.updatedGroups.set(groupId, updatedGroup);
    }
  }

  removeSheetFromGroupCommand(set, groupId, sheetId);
}
// #endregion

// #region get sheet group
export async function getSheetGroup(set: SheetManagementSet, groupId: string) {
  let group: SheetGroup | undefined =
    set.updatedGroups.get(groupId) ?? set.staticSheetGroups[groupId];

  if (!group) {
    group = await startReadSheetGroup(set, groupId);
  }

  return group;
}

const checkIfGroupExists = (set: SheetManagementSet, groupId: string) => {
  const entityIdToNameMap =
    set.updatedEntityIdToNameMap ?? set.staticEntityIdToNameMap;

  return !!entityIdToNameMap.groupsIdToName[groupId];
};
// #endregion
